import { useRef, useState } from "react";
import { NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import {
  ActionIcon,
  Button,
  FileButton,
  Group,
  Loader,
  Menu,
  Modal,
  Select,
  Stack,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconDotsVertical,
  IconExternalLink,
  IconGripVertical,
  IconLink,
  IconPencil,
  IconPhoto,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { uploadFile } from "@/features/page/services/page-service.ts";
import { getFileUrl } from "@/lib/config.ts";
import type {
  CardAttrs,
  CardField,
  CardTextField,
} from "@docmost/editor-ext";
import CardEditModal, { LinkFieldInput } from "./card-edit-modal";
import classes from "./cards.module.css";

function getTextField(
  fields: CardField[] | undefined,
  style: "title" | "description",
): CardTextField | null {
  if (!fields) return null;
  for (const f of fields) {
    if (
      f &&
      f.type === "text" &&
      f.visible !== false &&
      f.style === style
    ) {
      return f;
    }
  }
  return null;
}

export default function CardView(props: NodeViewProps) {
  const { node, editor, updateAttributes, deleteNode, selected } = props;
  const attrs = node.attrs as CardAttrs;
  const canEdit = editor.isEditable;
  const [modalOpen, setModalOpen] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  const [coverOpen, setCoverOpen] = useState(false);
  const [coverSrc, setCoverSrc] = useState(attrs.cover?.src ?? "");
  const [coverAlt, setCoverAlt] = useState(attrs.cover?.alt ?? "");
  const [coverAspect, setCoverAspect] = useState<string>(
    attrs.cover?.aspectRatio ?? "16/9",
  );
  const [coverUploading, setCoverUploading] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkHref, setLinkHref] = useState(attrs.targetLink?.href ?? "");
  const pageId = ((editor as any)?.storage?.pageId as string) ?? undefined;
  const resetRef = useRef<() => void>(null);

  const titleField = getTextField(attrs.fields, "title");
  const descriptionField = getTextField(attrs.fields, "description");

  const extraFields = (attrs.fields ?? []).filter((f) => {
    if (!f || f.visible === false) return false;
    if (f.type === "text" && (f.style === "title" || f.style === "description")) {
      return false;
    }
    return true;
  });

  const openCoverModal = () => {
    setCoverSrc(attrs.cover?.src ?? "");
    setCoverAlt(attrs.cover?.alt ?? "");
    setCoverAspect(attrs.cover?.aspectRatio ?? "16/9");
    setCoverOpen(true);
  };

  const commitCover = () => {
    const src = coverSrc.trim();
    if (!src) {
      updateAttributes({ cover: null });
    } else {
      updateAttributes({
        cover: { src, alt: coverAlt.trim() || "", aspectRatio: coverAspect },
      });
    }
    setCoverOpen(false);
  };

  const onFilePicked = async (file: File | null) => {
    if (!file) return;
    if (!pageId) {
      notifications.show({
        color: "red",
        message: "Cannot upload: no page context.",
      });
      return;
    }
    if (!file.type.includes("image/")) {
      notifications.show({ color: "red", message: "Only image files allowed." });
      return;
    }
    setCoverUploading(true);
    try {
      const att = (await uploadFile(file, pageId)) as unknown as {
        id?: string;
        fileName?: string;
      };
      // Build the URL the same way the <Image> extension does: a relative
      // /api/files/<id>/<name> — getFileUrl() resolves it through the
      // backend, which works for both local and S3 storage drivers.
      const srcUrl =
        att?.id && att?.fileName
          ? `/api/files/${att.id}/${att.fileName}`
          : "";
      if (!srcUrl) throw new Error("upload did not return attachment id");
      // Auto-commit on upload: user doesn't need to click Save.
      updateAttributes({
        cover: {
          src: srcUrl,
          alt: file.name,
          aspectRatio: coverAspect,
        },
      });
      setCoverSrc(srcUrl);
      setCoverAlt(file.name);
      setCoverOpen(false);
      notifications.show({ color: "teal", message: "Cover uploaded" });
    } catch (e: any) {
      notifications.show({
        color: "red",
        message: e?.response?.data?.message || "Upload failed",
      });
    } finally {
      setCoverUploading(false);
      resetRef.current?.();
    }
  };

  const openLinkModal = () => {
    setLinkHref(attrs.targetLink?.href ?? "");
    setLinkOpen(true);
  };

  const commitLink = () => {
    const href = linkHref.trim();
    if (!href) {
      updateAttributes({ targetLink: null });
    } else {
      const external = /^https?:\/\//i.test(href);
      updateAttributes({ targetLink: { href, external } });
    }
    setLinkOpen(false);
  };

  const onCardClick = (e: React.MouseEvent) => {
    if (!canEdit) return;
    if (e.metaKey || e.ctrlKey) return;
    e.preventDefault();
  };

  const startEditField = (f: CardTextField) => {
    if (!canEdit) return;
    setEditingFieldId(f.id);
    setEditingValue(f.value ?? "");
  };

  const saveEditField = () => {
    if (!editingFieldId) return;
    const next = (attrs.fields ?? []).map((x) =>
      x.id === editingFieldId && x.type === "text"
        ? ({ ...x, value: editingValue } as CardField)
        : x,
    );
    updateAttributes({ fields: next });
    setEditingFieldId(null);
  };

  const openTargetLink = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (attrs.targetLink?.href) {
      window.open(attrs.targetLink.href, "_blank", "noopener,noreferrer");
    }
  };

  const coverStyle = attrs.cover?.aspectRatio
    ? ({ ["--card-cover-ratio" as any]: attrs.cover.aspectRatio } as React.CSSProperties)
    : undefined;

  return (
    <NodeViewWrapper
      className={classes.wrapper}
      data-type="card"
      data-id={attrs.id || undefined}
    >
      <div
        className={classes.card}
        data-selected={selected ? "true" : undefined}
      >
        {attrs.cover?.src ? (
          <img
            src={
              /^https?:\/\//i.test(attrs.cover.src)
                ? attrs.cover.src
                : getFileUrl(attrs.cover.src)
            }
            alt={attrs.cover.alt ?? ""}
            className={classes.cover}
            style={coverStyle}
            loading="lazy"
            onClick={onCardClick}
          />
        ) : null}

        {canEdit && attrs.targetLink?.href ? (
          <button
            type="button"
            className={classes.targetLinkBadge}
            onClick={openTargetLink}
            onMouseDown={(e) => e.preventDefault()}
            aria-label={`Open ${attrs.targetLink.href}`}
            title={attrs.targetLink.href}
          >
            <IconExternalLink size={12} />
          </button>
        ) : null}

        <div className={classes.body}>
          {titleField ? (
            editingFieldId === titleField.id ? (
              <input
                className={classes.inlineInput}
                autoFocus
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onBlur={saveEditField}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    saveEditField();
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setEditingFieldId(null);
                  }
                }}
              />
            ) : (
              <div
                className={classes.title}
                onClick={() => startEditField(titleField)}
                title={canEdit ? "Click to edit" : undefined}
              >
                {titleField.value || "Untitled"}
              </div>
            )
          ) : (
            <div className={classes.title}>Untitled</div>
          )}
          {descriptionField ? (
            editingFieldId === descriptionField.id ? (
              <textarea
                className={classes.inlineTextarea}
                autoFocus
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onBlur={saveEditField}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setEditingFieldId(null);
                  }
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    saveEditField();
                  }
                }}
                rows={2}
              />
            ) : descriptionField.value ? (
              <div
                className={classes.description}
                onClick={() => startEditField(descriptionField)}
                title={canEdit ? "Click to edit" : undefined}
              >
                {descriptionField.value}
              </div>
            ) : canEdit ? (
              <div
                className={`${classes.description} ${classes.descriptionPlaceholder}`}
                onClick={() => startEditField(descriptionField)}
              >
                Add description…
              </div>
            ) : null
          ) : null}
          {extraFields.map((f) => (
            <div key={f.id} className={classes.field}>
              {f.label ? (
                <span className={classes.fieldLabel}>{f.label}:</span>
              ) : null}
              {f.type === "text" ? (
                <span>{f.value}</span>
              ) : f.type === "link" ? (
                <a
                  href={f.href}
                  target={f.external ? "_blank" : undefined}
                  rel={f.external ? "noopener noreferrer" : undefined}
                  onClick={onCardClick}
                >
                  {f.text || f.href}
                </a>
              ) : f.type === "files" ? (
                <span>
                  {(f.files ?? []).length} file
                  {(f.files ?? []).length === 1 ? "" : "s"}
                </span>
              ) : null}
            </div>
          ))}
        </div>

        {canEdit && (
          <div className={classes.actions} contentEditable={false}>
            <Tooltip label="Drag to reorder" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                data-drag-handle
                draggable="true"
                className={classes.dragHandle}
                aria-label="Drag card"
              >
                <IconGripVertical size={14} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Edit card" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => setModalOpen(true)}
                aria-label="Edit card"
              >
                <IconPencil size={14} />
              </ActionIcon>
            </Tooltip>
            <Menu shadow="md" position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label="Card menu"
                >
                  <IconDotsVertical size={14} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconLink size={14} />}
                  onClick={openLinkModal}
                >
                  {attrs.targetLink ? "Edit target link" : "Add target link"}
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconPhoto size={14} />}
                  onClick={openCoverModal}
                >
                  {attrs.cover ? "Edit cover" : "Add cover"}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={14} />}
                  onClick={() => deleteNode?.()}
                >
                  Delete card
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>
        )}
      </div>

      {canEdit && (
        <>
          <CardEditModal
            opened={modalOpen}
            onClose={() => setModalOpen(false)}
            node={node}
            editor={editor}
            getPos={props.getPos}
            updateAttributes={updateAttributes}
            deleteNode={() => deleteNode?.()}
          />

          <Modal
            opened={coverOpen}
            onClose={() => setCoverOpen(false)}
            title={attrs.cover ? "Replace cover" : "Add cover"}
            centered
            size="sm"
          >
            <Stack gap="md">
              <FileButton
                resetRef={resetRef}
                onChange={onFilePicked}
                accept="image/*"
              >
                {(props2) => (
                  <Button
                    fullWidth
                    variant="default"
                    leftSection={
                      coverUploading ? (
                        <Loader size="xs" />
                      ) : (
                        <IconUpload size={16} />
                      )
                    }
                    disabled={coverUploading}
                    {...props2}
                  >
                    {coverUploading
                      ? "Uploading…"
                      : attrs.cover
                        ? "Upload new image"
                        : "Upload image"}
                  </Button>
                )}
              </FileButton>

              {attrs.cover ? (
                <Button
                  variant="subtle"
                  color="red"
                  leftSection={<IconTrash size={14} />}
                  onClick={() => {
                    updateAttributes({ cover: null });
                    setCoverOpen(false);
                  }}
                >
                  Remove cover
                </Button>
              ) : null}
            </Stack>
          </Modal>

          <Modal
            opened={linkOpen}
            onClose={() => setLinkOpen(false)}
            title={attrs.targetLink ? "Edit target link" : "Add target link"}
            centered
            size="md"
          >
            <Stack gap="xs">
              <LinkFieldInput
                value={linkHref}
                onChange={(v) => setLinkHref(v)}
              />
              <Group justify="space-between">
                <Button
                  variant="subtle"
                  color="red"
                  onClick={() => {
                    updateAttributes({ targetLink: null });
                    setLinkOpen(false);
                  }}
                  disabled={!attrs.targetLink}
                >
                  Remove link
                </Button>
                <Group>
                  <Button variant="default" onClick={() => setLinkOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={commitLink}>Save</Button>
                </Group>
              </Group>
            </Stack>
          </Modal>
        </>
      )}
    </NodeViewWrapper>
  );
}
